import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import { Outlet, createBrowserRouter, RouterProvider, useLocation, useNavigation } from 'react-router-dom';
import './App.scss';
import Organization from './pages/Organization';
import SelectStaffLayout from './components/Layout/SelectStaffLayout';
import OperationPassword from './pages/PersonalCenter/OperationPassword';
import Information from './pages/PersonalCenter/Information';
import LoginPasswordForm from './pages/PersonalCenter/LoginPassword';
import Employee from './pages/Employee';
import CreateStaff from './pages/Staff/Create';
import StaffSelection from './pages/StaffSelection';
import Home from './pages/Home';
import RoleList from './pages/Role';
import { EditRole, EditOutLayer } from './pages/RolePermissions/Edit';
import BindOrganization from './pages/RolePermissions/Bind/BindOrganization';
import BindStaff from './pages/RolePermissions/Bind/BindStaff';
import BindDetail from './pages/RolePermissions/Bind/BindDetail';
import EmployeeAuthority from './pages/EmployeeAuthority';
import { PermissionsType } from './common/permissionsConst';

import QingFlowError from './pages/QingError';
import QlLanding from './pages/QlLanding';
import Qldata from './pages/Qldata';

import CreatePropMgmtDept from './pages/PropMgmtDept/Create';
import DetailPropMgmtDept from './pages/PropMgmtDept/Detail';
import HomeMange from './pages/HomeManage';
import OuMange from './pages/Employee/OuMange';
import HomeLanding from './pages/HomeLanding';
import RootRouteBoundary from './components/RootRouteBoundary';
import PropMgmtDeptList from './pages/PropMgmtDept/PropMgmtDeptList';
import NavigationPreferencePage from './pages/NavigationPreference';
import OrganizationManage from './pages/OrganizationManage';
import { AuthorizedPermission, CustomApp, getBase, useThemeContext } from '@maxtropy/components';
import CompositeNavConfig from './pages/CompositeNavConfig';
import CompositiveNavScreen from './pages/CompositeNavScreen';
import AccountSecurity from './pages/AccountSecurity';
import OverTime from './pages/OverTime';
import ExternalStaffManage from './pages/Employee/ExternalStaffManage';
import ExternalStaffDetail from './pages/Employee/ExternalStaffManage/detail';
import BatchConfigurePermissions from './pages/Employee/BatchConfigurePermissions';
import { MainOutlet } from './routes/outlets';

const RootWrapper = () => {
  const location = useLocation();
  const navigation = useNavigation();

  // 在微前端环境下，路由跳转时触发popstate事件，使主应用监听到路由变化
  useEffect(() => {
    if (!window.__MICRO_APP_ENVIRONMENT__) return;
    window.rawWindow.dispatchEvent(new PopStateEvent('popstate', { state: null }));
  }, [location]);

  useEffect(() => {
    if (navigation.state === 'idle' && window.__MICRO_APP_ENVIRONMENT__ && !window.__PAGE_LOAD_DISPATCHED__) {
      window.__PAGE_LOAD_DISPATCHED__ = true;
      window.rawWindow.dispatchEvent(
        new CustomEvent('micro-app-loaded', {
          detail: {
            name: 'ms-cc',
            data: { loaded: true },
          },
        })
      );
    }
  }, [navigation.state]);

  return <Outlet />;
};

const createNotFoundRoute = () => ({
  path: '*',
  loader: () => {
    throw new Response('Not Found', {
      status: 404,
      statusText: 'Not Found',
    });
  },
});

const router = createBrowserRouter(
  [
    // 使用 /* 而非 / 防止未匹配到出现错误
    {
      path: '/*',
      errorElement: <RootRouteBoundary />,
      element: <RootWrapper />,
      children: [
        {
          path: 'select-staff',
          element: (
            <SelectStaffLayout>
              <StaffSelection />
            </SelectStaffLayout>
          ),
        },
        {
          path: 'qingflow/error',
          element: <QingFlowError />,
        },
        {
          element: <MainOutlet />,
          children: [
            {
              index: true,
              element: <HomeLanding />,
            },
            {
              path: 'home',
              element: <Home />,
            },
            {
              // 轻流 节点数据权限
              path: 'ql-data-permissions/manage/approval',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_QL_APPROVE_DATA}>
                  <Qldata />
                </AuthorizedPermission>
              ),
            },
            {
              // 轻流页面中转页--平台应用
              path: 'ql/platform/:permissionCode',
              element: <QlLanding pageType="plantForm" />,
            },
            {
              // 轻流页面中转页--集成应用
              path: 'ql/integrated/:linkId',
              element: <QlLanding pageType="integrated" />,
            },
            {
              path: 'system/customer/info',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PCUSTOMERINFO}>
                  <Organization />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/customer/info/:mcid',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PUPDATEADDCUSTOMER}>
                  <OrganizationManage />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'user/settings/operatePassword',
              element: <OperationPassword />,
            },
            {
              path: 'user/settings/information',
              element: <Information />,
            },
            {
              path: 'user/settings/navigationPreference',
              element: <NavigationPreferencePage />,
            },
            {
              path: 'user/settings/loginPassword',
              element: <LoginPasswordForm />,
            },
            {
              path: 'system/customer/staff',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PSTAFFMANAGE}>
                  <Employee />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/customer/staff/create',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PCREATESTAFF}>
                  <CreateStaff />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/customer/staff/update',
              element: <Outlet />,
              children: [
                {
                  index: true,
                  element: (
                    <AuthorizedPermission permissionKey={PermissionsType.PUPDATESTAFF}>
                      <CreateStaff />
                    </AuthorizedPermission>
                  ),
                },
                {
                  path: ':id',
                  element: (
                    <AuthorizedPermission permissionKey={PermissionsType.PUPDATESTAFF}>
                      <CreateStaff />
                    </AuthorizedPermission>
                  ),
                },
              ],
            },
            {
              path: 'system/customer/staff/detail/:id',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PSTAFFINFO}>
                  <CreateStaff />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/customer/staff/authority/update/:id',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PUPDATESTAFF}>
                  <EmployeeAuthority />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/customer/staff/authority/detail/:id',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PSTAFFINFO}>
                  <EmployeeAuthority />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/customer/staff/external/update',
              element: <Outlet />,
              children: [
                {
                  index: true,
                  element: <ExternalStaffManage />,
                },
                {
                  path: ':id',
                  element: <ExternalStaffDetail isEdit />,
                },
              ],
            },
            {
              path: 'system/customer/staff/external/detail/:id',
              element: <ExternalStaffDetail />,
            },
            {
              path: 'system/customer/staff/external/batch',
              element: <BatchConfigurePermissions />,
            },
            {
              // ou
              path: 'system/customer/staff/ou/:id',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PSTAFFMANAGE}>
                  <OuMange />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEMANAGE}>
                  <RoleList />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/create',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PCREATEROLE}>
                  <EditRole />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/update/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PCREATEROLE}>
                  <EditRole />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/detail/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEINFO}>
                  <EditRole />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/permissions/update/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PUPDATEROLE}>
                  <EditOutLayer />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/permissions/detail/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEINFO}>
                  <EditOutLayer />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/permissions/bind/organization/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEBINDSTAFF}>
                  <BindOrganization />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/permissions/bind/staff/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEBINDSTAFF}>
                  <BindStaff />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/rolePer/role/permissions/bind/detail/:code',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEINFO}>
                  <BindDetail />
                </AuthorizedPermission>
              ),
            },
            // 物业管理
            {
              path: 'system/operation/prop-mgmt-dept',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_PROPERTYMANAGELIST}>
                  <PropMgmtDeptList />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/operation/prop-mgmt-dept/create',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_PROPERTYMANAGEADD}>
                  <CreatePropMgmtDept />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/operation/prop-mgmt-dept/edit/:id',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_PROPERTYMANAGEADD}>
                  <CreatePropMgmtDept isEdit />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/operation/prop-mgmt-dept/detail/:id',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_PROPERTYMANAGELIST}>
                  <DetailPropMgmtDept />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/operation/home-manage',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_HOME_PAGE_MANAGE}>
                  <HomeMange />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'nav-workbench/correlation/composite-nav-config',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_SYNTHESIZE_NAVIGATION_CONFIG}>
                  <CompositeNavConfig />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'nav-workbench/correlation/composite-nav-screen',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.P_SYNTHESIZE_NAVIGATION}>
                  <CompositiveNavScreen />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/account/security',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEMANAGE}>
                  <AccountSecurity />
                </AuthorizedPermission>
              ),
            },
            {
              path: 'system/account/overTime',
              element: (
                <AuthorizedPermission permissionKey={PermissionsType.PROLEMANAGE}>
                  <OverTime />
                </AuthorizedPermission>
              ),
            },
            createNotFoundRoute(),
          ],
        },
        createNotFoundRoute(),
      ],
    },
  ],
  {
    basename: getBase(),
  }
);

const App: React.FC = props => {
  const { setCurrentTheme, ...theme } = useThemeContext();

  useEffect(() => {
    const dataListener = (data: any) => {
      if (data?.theme) {
        setCurrentTheme?.(data.theme.theme || theme.theme); // 更新子应用的主题
      }
    };

    window.microApp?.addDataListener(dataListener, true);

    return () => {
      window.microApp?.removeDataListener(dataListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App">
      <ConfigProvider locale={zhCN} theme={theme}>
        <CustomApp style={{ height: '100%' }}>
          <RouterProvider router={router} />
        </CustomApp>
      </ConfigProvider>
    </div>
  );
};

export default App;
